// ProfileVideos — RunVideo + LipSync videolari grid
import { Play, Video } from 'lucide-react'
import { useState } from 'react'

export interface VideoItem {
  id: number | string
  title: string
  url: string
  thumbnail_url?: string
  duration?: number
  created_at?: string
}

interface Props {
  items: VideoItem[]
  loading?: boolean
}

function formatDuration(sec?: number): string {
  if (!sec) return ''
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function ProfileVideos({ items, loading }: Props) {
  const [playing, setPlaying] = useState<string | null>(null)

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #8b949e)', fontSize: 13 }}>Loading...</div>
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary, #8b949e)' }}>
        <Video size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <p style={{ fontSize: 14 }}>No videos yet</p>
      </div>
    )
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14,
    }}>
      {items.map(vid => (
        <div
          key={vid.id}
          style={{
            borderRadius: 14, overflow: 'hidden',
            border: '1px solid var(--border-default, #30363d)',
            background: 'rgba(255,255,255,0.02)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px)'
            e.currentTarget.style.boxShadow = '0 8px 28px rgba(102,126,234,0.12)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = 'none'
          }}
        >
          {/* Video / Thumbnail */}
          <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000' }}>
            {playing === String(vid.id) ? (
              <video
                src={vid.url}
                controls
                autoPlay
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <>
                {vid.thumbnail_url ? (
                  <img src={vid.thumbnail_url} alt={vid.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1c2128, #30363d)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Video size={28} style={{ color: '#667eea', opacity: 0.4 }} />
                  </div>
                )}
                {/* Play button overlay */}
                <button
                  onClick={() => setPlaying(String(vid.id))}
                  aria-label={`Play ${vid.title}`}
                  style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: 'rgba(102,126,234,0.9)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.4)',
                  }}>
                    <Play size={18} color="#fff" fill="#fff" />
                  </div>
                </button>
                {/* Duration badge */}
                {vid.duration && (
                  <span style={{
                    position: 'absolute', bottom: 8, right: 8, padding: '3px 8px',
                    borderRadius: 6, background: 'rgba(0,0,0,0.7)', color: '#fff',
                    fontSize: 11, fontWeight: 600,
                  }}>
                    {formatDuration(vid.duration)}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Title */}
          <div style={{ padding: '10px 14px' }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #e6edf3)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {vid.title}
            </h4>
            {vid.created_at && (
              <p style={{ fontSize: 11, color: 'var(--text-secondary, #8b949e)', margin: '4px 0 0' }}>
                {new Date(vid.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
